export type DueLite = {
  id: string;
  feeHeadName: string;
  periodLabel: string;
  dueDate: string;
  netAmount: number;
  paidAmount: number;
  outstanding: number;
  status: "PENDING" | "PARTIAL" | "PAID" | "CANCELLED";
  overdue: boolean;
};

/** Ticked dues and the amount typed for each (text, so the user can edit freely). */
export type Selection = Record<string, string>;
export type PaymentMode = "CASH" | "BANK_TRANSFER" | "CHEQUE" | "UPI";

export const PAYMENT_MODES: Array<{ id: PaymentMode; name: string }> = [
  { id: "CASH", name: "Cash" },
  { id: "BANK_TRANSFER", name: "Bank transfer" },
  { id: "CHEQUE", name: "Cheque" },
  { id: "UPI", name: "UPI" },
];

/** A due that can still take a payment. */
export const isOpenDue = (d: DueLite): boolean =>
  (d.status === "PENDING" || d.status === "PARTIAL") && d.outstanding > 0;

/** Oldest due date first, then by fee name, so "pay oldest first" is predictable. */
export function sortOldestFirst<T extends Pick<DueLite, "dueDate" | "feeHeadName">>(
  dues: readonly T[]
): T[] {
  return [...dues].sort(
    (a, b) => a.dueDate.localeCompare(b.dueDate) || a.feeHeadName.localeCompare(b.feeHeadName)
  );
}

/** Every open due ticked for its full outstanding amount (the common "pay everything" case). */
export function selectAll(dues: readonly DueLite[]): Selection {
  const out: Selection = {};
  for (const d of dues) if (isOpenDue(d)) out[d.id] = String(d.outstanding);
  return out;
}

/**
 * Spends `amount` on the oldest open dues first, each up to what it still needs. Whatever cannot
 * be spent (more than everything owed) is returned as `unallocated` instead of being lost.
 */
export function allocateOldestFirst(
  dues: readonly DueLite[],
  amount: number
): { selection: Selection; unallocated: number } {
  const selection: Selection = {};
  let left = Number.isFinite(amount) ? Math.max(0, Math.floor(amount)) : 0;
  for (const d of sortOldestFirst(dues.filter(isOpenDue))) {
    if (left <= 0) break;
    const pay = Math.min(left, d.outstanding);
    selection[d.id] = String(pay);
    left -= pay;
  }
  return { selection, unallocated: left };
}

const wholeNumber = (text: string): number | null =>
  /^\d+$/.test(text.trim()) ? Number(text.trim()) : null;

/** Sum of the valid typed amounts (invalid or empty ones count as 0). */
export function selectionTotal(selection: Selection): number {
  return Object.values(selection).reduce((sum, t) => sum + (wholeNumber(t) ?? 0), 0);
}

/** Per-due problems with the typed amount (empty map = all fine). */
export function selectionProblems(
  dues: readonly DueLite[],
  selection: Selection
): Record<string, string> {
  const byId = new Map(dues.map((d) => [d.id, d]));
  const problems: Record<string, string> = {};
  for (const [id, text] of Object.entries(selection)) {
    const due = byId.get(id);
    const n = wholeNumber(text);
    if (!due) problems[id] = "This due is no longer available";
    else if (n === null || n < 1) problems[id] = "Enter a whole number of rupees";
    else if (n > due.outstanding) problems[id] = `At most ${due.outstanding}`;
  }
  return problems;
}

/** Mirrors the API's request rules so the form can fail fast; the API stays authoritative. */
export function validateCollectForm(input: {
  mode: PaymentMode;
  reference: string;
  date: string;
  today: string;
  dues: readonly DueLite[];
  selection: Selection;
}): string | null {
  if (Object.keys(input.selection).length === 0) return "Tick at least one due to pay";
  if (Object.keys(selectionProblems(input.dues, input.selection)).length > 0)
    return "Fix the amounts marked in red";
  if (input.mode !== "CASH" && input.reference.trim() === "") {
    return "Enter the payment reference (UTR / cheque no. / UPI ref)";
  }
  if (!input.date) return "Choose the receipt date";
  if (input.date > input.today) return "The receipt date cannot be in the future";
  return null;
}

export function toAllocations(selection: Selection): Array<{ dueId: string; amount: number }> {
  return Object.entries(selection)
    .map(([dueId, text]) => ({ dueId, amount: wholeNumber(text) ?? 0 }))
    .filter((a) => a.amount > 0);
}
